import { useState, useEffect } from 'react'
import { useProject } from '@/contexts/project-context'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useGetToken } from '@/lib/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Plus, Calculator, TrendingUp, DollarSign } from 'lucide-react'
import ExitScenarioForm from '@/components/custom-ui/exit-scenario-form'
import ExitWaterfallCalculator from '@/components/custom-ui/exit-waterfall-calculator'

interface ExitScenario {
  id: string
  scenario_name: string
  scenario_type: string | null
  exit_valuation: number
  exit_date: string | null
  assumptions: any
  waterfall_results: any
  created_at: string | null
}

export default function ExitScenariosPage() {
  const getToken = useGetToken()
  const [scenarios, setScenarios] = useState<ExitScenario[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [selectedScenario, setSelectedScenario] = useState<ExitScenario | null>(null)
  const [showCalculator, setShowCalculator] = useState(false)
  const { currentProject } = useProject()

  useEffect(() => {
    if (currentProject) {
      fetchScenarios()
    }
  }, [currentProject])

  const fetchScenarios = async () => {
    setLoading(true)
    // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
      .from('exit_scenarios')
      .select('*')
      .eq('project_id', currentProject?.id || '')
      .order('created_at', { ascending: false })

    if (data && !error) {
      setScenarios(data)
    }
    setLoading(false)
  }

  const handleScenarioClick = (scenario: ExitScenario) => {
    setSelectedScenario(scenario)
    setShowCalculator(true)
  }

  const getScenarioIcon = (type: string) => {
    switch (type) {
      case 'acquisition':
        return <TrendingUp className="h-5 w-5" />
      case 'ipo':
        return <DollarSign className="h-5 w-5" />
      default:
        return <Calculator className="h-5 w-5" />
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Exit Scenarios</h1>
          <p className="text-gray-600 mt-1">Model exit events and calculate waterfall distributions</p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Scenario
        </Button>
      </div>

      {scenarios.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Calculator className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No exit scenarios yet</h3>
            <p className="text-gray-500 mb-4">Create exit scenarios to model potential liquidity events</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Scenario
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((scenario) => (
            <Card 
              key={scenario.id} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleScenarioClick(scenario)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {getScenarioIcon(scenario.scenario_type || '')}
                    {scenario.scenario_name}
                  </CardTitle>
                  <span className="text-xs text-gray-500 capitalize">
                    {scenario.scenario_type}
                  </span>
                </div>
                <CardDescription>
                  {scenario.exit_date && new Date(scenario.exit_date).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Exit Valuation</span>
                    <span className="text-lg font-semibold">{formatCurrency(scenario.exit_valuation)}</span>
                  </div>
                  {scenario.waterfall_results && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-gray-600 mb-1">Top Recipients:</div>
                      {scenario.waterfall_results.distributions?.slice(0, 3).map((dist: any, idx: number) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span className="text-gray-600">{dist.name}</span>
                          <span className="font-medium">{formatCurrency(dist.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <ExitScenarioForm
          open={showForm}
          onOpenChange={setShowForm}
          onSuccess={fetchScenarios}
        />
      )}

      {showCalculator && selectedScenario && (
        <ExitWaterfallCalculator
          open={showCalculator}
          onOpenChange={setShowCalculator}
          scenario={selectedScenario}
          onUpdate={fetchScenarios}
        />
      )}
    </div>
  )
}